import { useEffect, useRef } from "react"
import { BoundingBox } from "@/types/util"
import debounce from "lodash/debounce"

import { useLazyGetMapWeatherQuery } from "@/lib/storage/api/place-api"

const MAP_WEATHER_MIN_ZOOM = 10
const MAP_WEATHER_MAX_BBOX_DEG = 0.75 // ~80km at equator, matches server-side cap

export function useMapWeather({
  bbox,
  zoom,
  enabled,
  bboxFormat = "swLngSwLatNeLngNeLat",
}: {
  bbox?: BoundingBox
  zoom: number
  enabled: boolean
  bboxFormat?: "swLngSwLatNeLngNeLat" | "minLatMaxLatMinLongMaxLong"
}) {
  const [triggerMapWeather, { data: mapWeatherResponse }] =
    useLazyGetMapWeatherQuery()

  const debouncedFetchMapWeather = useRef(
    debounce(
      (args: {
        swLat: number
        swLng: number
        neLat: number
        neLng: number
        zoom: number
      }) => {
        void triggerMapWeather(args)
      },
      350
    )
  ).current

  useEffect(() => {
    return () => debouncedFetchMapWeather.cancel()
  }, [debouncedFetchMapWeather])

  const shouldShowWeatherForZoom = zoom >= MAP_WEATHER_MIN_ZOOM

  useEffect(() => {
    if (!bbox || !enabled || !shouldShowWeatherForZoom) {
      debouncedFetchMapWeather.cancel()
      return
    }

    const [swLng, swLat, neLng, neLat] =
      bboxFormat === "minLatMaxLatMinLongMaxLong"
        ? [bbox[2], bbox[0], bbox[3], bbox[1]]
        : bbox

    if (
      !Number.isFinite(swLat) ||
      !Number.isFinite(swLng) ||
      !Number.isFinite(neLat) ||
      !Number.isFinite(neLng) ||
      swLat >= neLat ||
      swLat < -90 ||
      neLat > 90 ||
      swLng < -180 ||
      swLng > 180 ||
      neLng < -180 ||
      neLng > 180
    ) {
      debouncedFetchMapWeather.cancel()
      return
    }

    const latSpan = neLat - swLat
    const lngSpan = neLng - swLng
    if (
      latSpan > MAP_WEATHER_MAX_BBOX_DEG ||
      lngSpan > MAP_WEATHER_MAX_BBOX_DEG
    ) {
      debouncedFetchMapWeather.cancel()
      return
    }

    void debouncedFetchMapWeather({
      swLat,
      swLng,
      neLat,
      neLng,
      zoom,
    })
  }, [
    bbox,
    bboxFormat,
    zoom,
    enabled,
    shouldShowWeatherForZoom,
    debouncedFetchMapWeather,
  ])

  return {
    currentWeather: mapWeatherResponse?.weather?.current,
    shouldShowWeatherForZoom,
  }
}
